import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {FromServerNLADVersionedData} from 'runs/AttachmentConstants';
import {EntriesByRunId} from 'runs/inbox/RunsInboxModalConstants';
import {refetchRun} from 'runs/withMaybeRunFetching';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {SearchParams, SearchResults} from 'search/SearchConstants';
import {postSearchRequest} from 'search/SearchDAO';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {AssayResultValidation, AssayRunValidation} from '../validation/ValidationConstants';
import {AssayResult, AssayRun} from './AssayRecordConstants';

export const RUNS_SEARCH_URL = '/1/api/assay-runs:search';

export interface RunsSearchResult extends SearchResults {
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
}

export function listAssayRuns(searchParams: SearchParams, level: string): Promise<RunsSearchResult> {
  return postSearchRequest({
    url: RUNS_SEARCH_URL,
    data: {...searchParams, level},
  });
}

// TODO(BNCH-57692): Remove once ENABLE_SIGHTGLASS_RUNS_SEARCH is enabled everywhere
export function legacyListAssayRuns({
  schemaId,
  nextToken,
  level,
  originType,
  isAvailable,
  isArchived,
  query,
}: {
  schemaId: string;
  nextToken: Optional<string>;
  level: string;
  originType?: string;
  isAvailable: boolean;
  isArchived: boolean;
  query: string;
}): Promise<{
  assayRuns: Array<AssayRun>;
  entriesByRunId: EntriesByRunId;
  assayResultSchemasByRunId: {[warehouseId: string]: Array<AssaySchema>};
  nextToken: string | null;
}> {
  return Promise.resolve(
    net.getJSON('/1/api/assay-runs', {
      assaySchemaId: schemaId,
      level,
      nextToken,
      originType,
      isAvailable,
      isArchived,
      query,
    })
  );
}

export interface ListAssayResultsResponse {
  assayResults: Array<AssayResult>;
  nextToken: string;
}

export function listAssayResults({
  schemaId,
  nextToken,
  excludedSchemaIds,
  bioentityId,
  requestId,
  storableId,
  studyId,
  assayRunId,
  sort,
}: {
  schemaId?: string;
  nextToken: string;
  excludedSchemaIds?: Array<string>;
  bioentityId?: string;
  requestId?: string;
  storableId?: string;
  studyId?: string;
  assayRunId?: string;
  sort?: string;
}): Promise<ListAssayResultsResponse> {
  return Promise.resolve(
    net.getJSON('/1/api/assay-results', {
      assaySchemaId: schemaId,
      excludedSchemaIds: (excludedSchemaIds || []).join(','),
      nextToken,
      bioentityId,
      requestId,
      storableId,
      studyId,
      assayRunId,
      sort,
    })
  );
}

export interface CountResultsBySchemaResponse {
  resultCountBySchemaId: {[schemaId: string]: number};
}

export function countResultsBySchema({
  assayRunId,
  bioentityId,
  requestId,
  storableId,
  studyId,
}: {
  assayRunId?: string;
  bioentityId?: string;
  requestId?: string;
  storableId?: string;
  studyId?: string;
}): Promise<CountResultsBySchemaResponse> {
  return Promise.resolve(
    net.getJSON('/1/api/assay-results:count-results-by-schema', {
      assayRunId,
      bioentityId,
      requestId,
      storableId,
      studyId,
    })
  );
}

export function fetchAssayRuns(assayRunIds: Array<string>): Promise<{
  assayRunsById: {[runId: string]: AssayRun};
}> {
  return Promise.resolve(net.getJSON(`/1/api/assay-runs:bulk-get?assayRunIds=${assayRunIds.join(',')}`));
}

export async function fetchAssayResultsByRun(
  assayRunId: string,
  nextToken: string = '',
  {excludedSchemaIds}: {excludedSchemaIds: Array<string>}
): Promise<{
  assayResults: Array<AssayResult>;
  nextToken: string;
}> {
  await refetchRun(assayRunId);
  return net.getJSON(
    `/1/api/assay-results?assayRunId=${assayRunId}&nextToken=${nextToken}&excludedSchemaIds=${(
      excludedSchemaIds || []
    ).join(',')}`
  );
}

export function fetchAssayResults(assayResultIds: Array<string>): Promise<{
  assayResultsById: {[resultId: string]: AssayResult};
}> {
  return Promise.resolve(
    net.postJSON({
      url: '/1/api/assay-results:bulk-get',
      data: {assayResultIds},
    })
  );
}

export function updateAssayRun(
  assayRunId: string,
  assayRunData: {
    tags: TagUpdateObject;
    connectionId: string | null;
    equipmentId: string | null;
    studyIds: Array<string>;
  }
): Promise<{
  assayRun: AssayRun;
}> {
  return net.patchJSONPromise({
    url: `/1/api/assay-runs/${assayRunId}`,
    data: assayRunData,
  });
}

export function archiveAssayRuns(
  assayRunIds: Array<string>,
  purpose: string
): Promise<{
  archivedItemIds: Array<string>;
}> {
  return net.postJSONPromise({
    url: '/1/api/assay-runs:bulk-archive',
    data: {ids: assayRunIds, purpose},
  });
}

export function unarchiveAssayRuns(assayRunIds: Array<string>): Promise<{
  unarchivedItemIds: Array<string>;
}> {
  return net.postJSONPromise({
    url: '/1/api/assay-runs:bulk-unarchive',
    data: {ids: assayRunIds},
  });
}

export function bulkFetchAssayRunValidation(assayRunIds: Array<string>): Promise<{
  [runId: string]: AssayRunValidation;
}> {
  return net.postJSONPromise({
    url: '/1/api/assay-runs:bulk-fetch-assay-run-validation',
    data: {assayRunIds},
  });
}

export function bulkFetchAssayResultValidation(assayResultIds: Array<string>): Promise<{
  [runId: string]: AssayResultValidation;
}> {
  return net.postJSONPromise({
    url: '/1/api/assay-results:bulk-fetch-assay-result-validation',
    data: {assayResultIds},
  });
}

export function exportResults(
  schemaId: string,
  {
    bioentityId,
    requestId,
    storableId,
    studyId,
  }: {bioentityId?: string; requestId?: string; storableId?: string; studyId?: string}
): JQueryPromise<Response> {
  return net
    .postJSON({
      url: '/1/api/assay-results:export',
      data: {
        assaySchemaId: schemaId,
        bioentityId,
        requestId,
        storableId,
        studyId,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function exportResultsById(ids: Array<string>): JQueryPromise<Response> {
  return net
    .postJSON({
      url: '/1/api/assay-results:export-by-ids',
      data: {
        ids,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function exportRunResults(assayRunId: string, resultSchemaId: string): JQueryPromise<Response> {
  return net
    .postJSON({
      url: '/1/api/assay-runs:export-results',
      data: {
        assayRunId,
        resultSchemaId,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function createVersionedDataForRun(
  runId: string,
  entryId: string
): Promise<FromServerNLADVersionedData> {
  return net.postJSONPromise({
    url: `/1/api/note-linked-assay-datas/create-versioned-data-for-run/${runId}`,
    data: {fileId: entryId},
  });
}

export interface OversizedNLADTable {
  rows: Array<StructuredTableRow<{}>>;
  url: string;
}

export function fetchOversizedNLADTable(structuredTableId: string): Promise<OversizedNLADTable> {
  return net.getJSONPromise(`/1/api/note-linked-assay-datas/${structuredTableId}/get-oversized-nlad-table`, {
    structuredTableId,
  });
}

/**
 * Create an assay run and simultaneously patch it into the entry in the given
 * NoteLinkedAssayData note part. By creating and patching simultaneously, we
 * can avoid any intermediate state where an assay run exists outside the entry.
 * This creates a new entry version, so the caller must refresh the entry
 * afterwards.
 *
 * @param entryOrTemplateId - the entry, stage entry or template containing the NLAD
 * @param noteLinkedAssayDataProvenanceUuid - the provenanceUuid of the NLAD to assign the run to
 * @param assayRunSchemaId - the AssayRunSchema that we want to create an instance of
 * @param tags - the tags to assign to the run after it's been created
 * @param actionFunctionContext - The context in which the AssayRun was created and added to the entry/document
 */
export function createAssayRunAndPatchEntry(
  entryOrTemplateId: string,
  noteLinkedAssayDataProvenanceUuid: string,
  assayRunSchemaId: string,
  tags: TagUpdateObject,
  connectionId: string | null,
  equipmentId: string | null,
  studyIds: Array<string>,
  actionFunctionContext: ActionFunctionContext
): Promise<void> {
  return net.postJSONPromise({
    url: `/1/api/note-linked-assay-datas/${entryOrTemplateId}/${noteLinkedAssayDataProvenanceUuid}/create-run`,
    data: {assayRunSchemaId, tags, connectionId, equipmentId, studyIds, actionFunctionContext},
  });
}

export function archiveAutomationOutputFile(
  automationOutputFileId: string,
  archivePurpose: string
): Promise<{
  automationOutputFile: AutomationOutputFileGraphQL;
  linkedResultCount: number;
}> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/assay-runs:archive-automation-output-file',
    data: {
      automationOutputFileId,
      archivePurpose,
    },
  });
}

export function unarchiveAutomationOutputFile(automationOutputFileId: string): Promise<{
  automationOutputFile: AutomationOutputFileGraphQL;
  linkedResultCount: number;
}> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/assay-runs:unarchive-automation-output-file',
    data: {
      automationOutputFileId,
    },
  });
}

export function runURL(runId: string): string {
  return `/assay-runs/${runId}`;
}
